// src/utils/quote/quoteAttachments.js
//
// Compression + upload pipeline for pumppuRequest and pumppuOffer attachments.
// Ported from puminet4/src/components/attachments/AttachmentFunctions.jsx —
// only the pieces betonijerry needs (no keikka/vehicle/person variants).
//
// Flow:
//   pick file → validate → compress (images only) → GET /generateSASToken →
//   PUT to Azure blob → POST metadata to parameterized metadata endpoint
//
// Caller is responsible for owning the `pumppuRequestId` (RequestForm pre-claims
// the server draft on step 4 → 5 transition; ValuDetail reads it from the route).

import imageCompression from "browser-image-compression";
import { API_URL } from "src/utils/apiUrl";
import { authHeaders, jsonOrThrow } from "src/utils/apiFetch";
import { fi } from "src/i18n/fi";
import { lookup } from "src/i18n";

const VALID_FILE_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const COMPRESSIBLE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 50 * 1024 * 1024; // 50 MB pre-compression ceiling
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20 MB for non-image (PDF)
// Falls back to the BetoniJerry umbrella tenant when the authenticated person
// has no ownerAsiakasId yet (returning users predating source:"betonijerry"
// auto-attach). Without this the blob path becomes "images/null/<year>/…".
const UMBRELLA_ASIAKAS_ID = 1349;

// i18n: this module has no React/hook access — callers thread the caller's
// useT() result in explicitly as `t`. `t` defaults to a Finnish-bundle lookup
// so out-of-wizard callers that don't pass `t` (e.g. OfferAttachments.jsx on
// the provider side, not part of this task) keep their current Finnish-only
// behavior unchanged rather than crashing or showing raw key strings.
// These per-error strings DO surface individually in English: every throw below
// goes through localizedError(), and resolveErrorMessage renders a flagged
// error's message as-is instead of collapsing it to `attachments.uploadFailed`.
// (Before that flag existed, the English path discarded err.message wholesale to
// keep Finnish server prose out of the UI, and took every one of these with it.)
const defaultT = (key) => lookup(fi, key) ?? key;

export function validateFile(file, t = defaultT) {
    if (!file) return t("wizard.attachmentsUpload.noFileSelected");
    if (!VALID_FILE_TYPES.includes(file.type)) {
        return t("wizard.attachmentsUpload.unsupportedType");
    }
    return null;
}

/**
 * Error whose message is already translated (built here with t(), in the caller's
 * language). The `localized` flag tells resolveErrorMessage to render the message
 * as-is instead of falling back to generic copy — see src/i18n/errorCopy.js. The
 * flag is what separates our own copy from Finnish server prose, which the English
 * path must keep discarding.
 */
function localizedError(message) {
    const err = new Error(message);
    err.localized = true;
    return err;
}

export async function compressImageFile(file, t = defaultT) {
    if (COMPRESSIBLE_TYPES.includes(file.type)) {
        if (file.size > MAX_IMAGE_BYTES) {
            throw localizedError(t("wizard.attachmentsUpload.imageTooLarge"));
        }
        return imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
        });
    }
    if (file.size > MAX_UPLOAD_BYTES) {
        throw localizedError(t("wizard.attachmentsUpload.fileTooLarge"));
    }
    return file;
}

export function getImageDimensions(file) {
    if (!file?.type?.startsWith("image/")) return Promise.resolve(null);
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
            URL.revokeObjectURL(url);
        };
        img.onerror = () => {
            resolve(null);
            URL.revokeObjectURL(url);
        };
        img.src = url;
    });
}

function extensionFor(mimeType) {
    if (mimeType === "image/jpeg") return "jpg";
    if (mimeType === "image/png") return "png";
    if (mimeType === "image/webp") return "webp";
    if (mimeType === "application/pdf") return "pdf";
    return "bin";
}

function uuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    // Fallback — only hit on ancient browsers; cryptographic strength not required for blob names.
    return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

async function getSasUploadUrl({ fileFolder, fileName }, t = defaultT) {
    const url = `${API_URL}/generateSASToken?fileFolder=${encodeURIComponent(fileFolder)}&fileName=${encodeURIComponent(fileName)}`;
    const res = await fetch(url);
    if (!res.ok) throw localizedError(t("wizard.attachmentsUpload.sasFetchFailed"));
    const data = await res.json();
    if (!data?.uploadUrl) throw localizedError(t("wizard.attachmentsUpload.sasEmpty"));
    return data.uploadUrl;
}

async function putToAzure(uploadUrl, file, t = defaultT) {
    const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob" },
        body: file,
    });
    if (!res.ok) throw localizedError(t("wizard.attachmentsUpload.azureUploadFailed"));
}

// Shared SAS → Azure PUT → metadata-POST core. `metadataPath` is the only
// per-entity difference between pumppuRequest and pumppuOffer uploads.
async function uploadAttachmentCore({
    file,
    metadataPath,
    token,
    ownerAsiakasId,
    entryByPersonId,
    fileComment = null,
    attachmentGroupId,
    attachmentTypeId,
    t = defaultT,
}) {
    const error = validateFile(file, t);
    if (error) throw localizedError(error); // validateFile already returns t()-resolved copy

    const prepared = await compressImageFile(file, t);
    const dimensions = await getImageDimensions(prepared);

    const effectiveOwnerAsiakasId = ownerAsiakasId || UMBRELLA_ASIAKAS_ID;
    const newFileName = `${uuid()}.${extensionFor(prepared.type)}`;
    const fileFolder = `${effectiveOwnerAsiakasId}/${new Date().getFullYear()}`;

    const uploadUrl = await getSasUploadUrl({ fileFolder, fileName: newFileName }, t);
    await putToAzure(uploadUrl, prepared, t);

    const body = {
        entryByPersonId,
        ownerAsiakasId: effectiveOwnerAsiakasId,
        origFileName: file.name,
        fileName: newFileName,
        fileType: prepared.type,
        fileUrl: uploadUrl,
        fileFolder,
        fileComment,
        fileSize: prepared.size,
        fileETag: "0",
        attachmentGroupId: attachmentGroupId ?? 1,
        attachmentTypeId: attachmentTypeId ?? 1,
        imageWidth: dimensions?.width ?? null,
        imageHeight: dimensions?.height ?? null,
    };

    const res = await fetch(`${API_URL}${metadataPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify(body),
    });
    const result = await jsonOrThrow(res);
    return {
        attachmentId: result?.attachmentId,
        fileName: newFileName,
        origFileName: file.name,
        fileType: prepared.type,
        fileSize: prepared.size,
        imageWidth: dimensions?.width ?? null,
        imageHeight: dimensions?.height ?? null,
    };
}

/**
 * Upload a single file to a pumppuRequest.
 *
 * @param {{
 *   file: File,
 *   pumppuRequestId: number,
 *   token: string,
 *   ownerAsiakasId: number,
 *   entryByPersonId: number,
 *   fileComment?: string,
 *   t?: (key: string) => string,
 * }} args
 * @returns {Promise<{ attachmentId: number, fileName: string, origFileName: string, fileType: string }>}
 */
export function uploadPumppuRequestAttachment({ pumppuRequestId, ...rest }) {
    return uploadAttachmentCore({
        ...rest,
        metadataPath: `/api/attachments/pumppuRequest/upload/${pumppuRequestId}`,
    });
}

/**
 * Upload a single file to the operator's pumppuOffer (draft must exist —
 * the backend stamps both pumppuOfferId and the derived pumppuRequestId).
 * Same args as uploadPumppuRequestAttachment but keyed by pumppuOfferId.
 */
export function uploadPumppuOfferAttachment({ pumppuOfferId, ...rest }) {
    return uploadAttachmentCore({
        ...rest,
        metadataPath: `/api/attachments/pumppuOffer/upload/${pumppuOfferId}`,
    });
}

/**
 * Upload a company logo — attachmentTypeId 11 ("Asiakkaan logo"), group 2 (ASIAKAS).
 * Same mechanism puminet4's AsiakasLogoUploader writes, so a logo uploaded here shows up
 * in betoni.online's asiakas liitteet and vice versa.
 *
 * NOTE the route shape: unlike this file's pumppuRequest/pumppuOffer helpers, asiakas
 * attachments go through the generic POSITIONAL-slot endpoint with the literal string
 * "null" in every unused slot. There is no /api/attachments/asiakas/upload/:id.
 *
 * Consumed by the public provider pages, which download the bytes at BUILD time — the blob
 * URL is SAS-signed with a 1 h expiry and cannot be embedded in generated static HTML.
 *
 * @param {{ asiakasId: number, file: File, ownerAsiakasId: number, entryByPersonId: number }} args
 */
export function uploadAsiakasLogo({ asiakasId, ...rest }) {
    return uploadAttachmentCore({
        ...rest,
        metadataPath: `/api/attachments/upload/null/null/${asiakasId}/null/null/null/null`,
        attachmentGroupId: 2,
        attachmentTypeId: 11,
    });
}

/** Returns enriched rows with fresh blob URLs (used by AttachmentsSection thumbnails). */
export async function listPumppuRequestAttachments({ pumppuRequestId, token }) {
    const res = await fetch(
        `${API_URL}/api/attachments/pumppuRequest/urls/${pumppuRequestId}`,
        { headers: authHeaders(token) },
    );
    return jsonOrThrow(res);
}

export async function deleteAttachment({ attachmentId, token }) {
    const res = await fetch(`${API_URL}/api/attachments/delete/${attachmentId}`, {
        method: "DELETE",
        headers: authHeaders(token),
    });
    return jsonOrThrow(res);
}
